import React, { useState } from 'react';
import { Check, Zap } from 'lucide-react';

const FREE_FEATURES = [
  'Manually add transactions',
  'Manually add account balances',
  'Track bills & recurring expenses',
  'Budget categories + 50/30/20 tracking',
  'All planning & wealth tools',
];

const PRO_FEATURES = [
  'Everything in Free',
  'Auto-sync bank accounts via Plaid',
  'Automatic transaction import',
  'Real-time balance updates',
  'Smart auto-categorization from live data',
];

export default function ChoosePlan({ onSelectFree, onJoinWaitlist }) {
  const [waitlistDone, setWaitlistDone] = useState(false);

  const handleWaitlist = () => {
    setWaitlistDone(true);
    onJoinWaitlist(); // parent saves to Supabase + closes after 2s
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 flex flex-col items-center justify-center p-6 overflow-y-auto">

      {/* Logo */}
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-900/50">
          <span className="text-white font-bold text-base">F</span>
        </div>
        <span className="font-bold text-slate-100 text-xl">Finovo</span>
      </div>

      {/* Heading */}
      <div className="text-center mb-10 max-w-xl">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-100 mb-3 leading-tight">
          Choose your plan
        </h1>
        <p className="text-slate-400 text-base leading-relaxed">
          Start for free — all tools included. Pro with live bank sync is coming soon.
        </p>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 w-full max-w-2xl">

        {/* ── Free card ── */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-7 flex flex-col">
          <div className="mb-6">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">
              Starter
            </div>
            <div className="flex items-end gap-2 mb-1">
              <span className="text-4xl font-bold text-slate-100">Free</span>
            </div>
            <div className="text-sm text-slate-400">Forever — no credit card needed</div>
          </div>

          <ul className="space-y-3 mb-8 flex-1">
            {FREE_FEATURES.map(f => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                <Check size={15} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>

          <button
            onClick={onSelectFree}
            className="w-full py-3 bg-blue-600 hover:bg-blue-500 active:scale-[0.98] text-white font-semibold rounded-xl transition-all text-sm"
          >
            Get Started Free
          </button>
        </div>

        {/* ── Pro card ── */}
        <div className="relative bg-slate-800 border border-blue-500/50 rounded-2xl p-7 flex flex-col shadow-2xl shadow-blue-900/30">

          {/* Coming Soon badge */}
          <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
            <span className="inline-flex items-center gap-1.5 bg-blue-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg shadow-blue-900/40">
              <Zap size={10} /> Coming Soon
            </span>
          </div>

          <div className="mb-6 mt-2">
            <div className="text-xs font-semibold text-blue-400 uppercase tracking-widest mb-3">
              Connected
            </div>
            <div className="flex items-end gap-2 mb-1">
              <span className="text-4xl font-bold text-slate-100">Pro</span>
            </div>
            <div className="text-sm text-slate-400">Pricing TBD — join the waitlist</div>
          </div>

          <ul className="space-y-3 mb-8 flex-1">
            {PRO_FEATURES.map(f => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                <Check size={15} className="text-blue-400 flex-shrink-0 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>

          {waitlistDone ? (
            <div className="w-full py-3 bg-emerald-900/40 border border-emerald-700/50 text-emerald-300 font-semibold rounded-xl text-sm text-center flex items-center justify-center gap-2">
              <Check size={15} />
              You're on the list!
            </div>
          ) : (
            <button
              onClick={handleWaitlist}
              className="w-full py-3 bg-slate-700 hover:bg-blue-900/40 border border-slate-600 hover:border-blue-500/60 text-slate-200 font-semibold rounded-xl transition-all text-sm active:scale-[0.98]"
            >
              Join Waitlist
            </button>
          )}
        </div>
      </div>

      {/* Footer note */}
      <p className="text-xs text-slate-600 mt-8 text-center">
        You can change or upgrade your plan anytime from Settings.
      </p>
    </div>
  );
}
